import { useEffect, useState } from 'react';
import type { CompactPuzzle, Difficulty } from '@ricochet/game-engine';
import { fetchDailyPuzzle } from '../api/puzzles.ts';
import { useAuth } from './useAuth.ts';

export interface DailyStats {
  puzzleId: string;
  par: number;
  likeCount: number;
  solveCount: number;
  overallBest: number | null;
  userBest?: number | null;
  solved?: boolean;
  liked?: boolean;
}

interface DailyResult {
  puzzle: CompactPuzzle;
  stats: DailyStats;
}

let cached: DailyResult | null = null;
let inFlight: Promise<DailyResult | null> | null = null;
let cacheInvalid = false;
let cachedWithAuth = false;

export function invalidateDailyPuzzleCache() {
  cacheInvalid = true;
}

async function loadFromApi(token?: string): Promise<DailyResult | null> {
  const data = await fetchDailyPuzzle(token);
  const puzzle: CompactPuzzle = {
    id: data.id,
    name: data.name,
    encoded: data.encoded,
    gridSize: data.gridSize,
    numTargets: data.numTargets,
    difficulty: data.difficulty as Difficulty,
    minMoves: data.par,
  };
  return {
    puzzle,
    stats: {
      puzzleId: data.id,
      par: data.par,
      likeCount: data.likeCount,
      solveCount: data.solveCount,
      overallBest: data.overallBest,
      userBest: data.userBest,
      solved: data.solved,
      liked: data.liked,
    },
  };
}

async function loadDaily(token?: string): Promise<DailyResult | null> {
  try {
    return await loadFromApi(token);
  } catch {
    return null;
  }
}

export function useDailyPuzzle() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth();
  const [puzzle, setPuzzle] = useState<CompactPuzzle | null>(cached?.puzzle ?? null);
  const [stats, setStats] = useState<DailyStats | null>(cached?.stats ?? null);
  const [generating, setGenerating] = useState(!cached);

  useEffect(() => {
    // If authenticated but cache was fetched without auth, need fresh fetch with token
    if (cached && !cacheInvalid && isAuthenticated && !cachedWithAuth) {
      cacheInvalid = true;
    }

    if (cached && !cacheInvalid) return;
    if (cacheInvalid) {
      cached = null;
      inFlight = null;
      cacheInvalid = false;
    }

    let cancelled = false;

    async function fetchAndStore() {
      let token: string | undefined;
      if (isAuthenticated) {
        try { token = await getAccessTokenSilently(); } catch { /* best-effort */ }
      }

      if (!inFlight) {
        cachedWithAuth = !!token;
        inFlight = loadDaily(token);
      }

      const result = await inFlight;
      if (cancelled) return;
      if (result) {
        cached = result;
        setPuzzle(result.puzzle);
        setStats(result.stats);
      }
      setGenerating(false);
    }

    fetchAndStore();
    return () => { cancelled = true; };
  }, [isAuthenticated, getAccessTokenSilently]);

  return { puzzle, stats, generating };
}
